import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Play, Globe, GlobeLock, Shield, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api, { apiError } from "../lib/api";
import { computeModel } from "../lib/deckModel";
import { DeckPresenter } from "../components/decks/DeckPresenter";
import { ProjectPanel, ExitsPanel, VersionPanel } from "../components/decks/BuilderPanels";
import { DeckShell } from "../components/decks/DeckShell";
import { AccessDialog } from "../components/decks/AccessDialog";
import { DeckViewer } from "./PublicDeck";

const STATUSES = ["Draft", "Internal Review", "Partner Review", "Investor Review", "City / Government Review", "Published", "Archived"];

export function DeckPreview() {
  const { id } = useParams();
  const [deck, setDeck] = useState(null);
  useEffect(() => { api.get(`/decks/${id}`).then((r) => setDeck(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  if (!deck) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  return <DeckViewer deck={{ ...deck, sections: deck.sections.filter((s) => !s.hidden) }} preview />;
}

export default function DeckBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [deck, setDeck] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(true);
  const [library, setLibrary] = useState([]);
  const [accessOpen, setAccessOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [presenting, setPresenting] = useState(params.get("present") === "1");

  useEffect(() => {
    api.get(`/decks/${id}`).then((r) => setDeck(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/decks/meta").then((r) => setLibrary(r.data.library || [])).catch(() => {});
  }, [id]);
  const patch = useCallback((p) => { setDeck((d) => ({ ...d, ...p })); setDirty(true); }, []);
  const save = useCallback(async (quiet) => {
    if (!deck) return null;
    if (document.activeElement?.blur) document.activeElement.blur();
    await new Promise((r) => setTimeout(r, 0));
    setSaving(true);
    try {
      const cur = await new Promise((r) => setDeck((d) => { r(d); return d; }));
      const { data } = await api.put(`/decks/${id}`, { project: cur.project, assumptions: cur.assumptions, exits: cur.exits, sections: cur.sections, status: cur.status, embed_url: cur.embed_url || "" });
      setDeck(data); setDirty(false); if (!quiet) toast.success(`Saved · revision ${data.revision}`);
      return data;
    } catch (e) { toast.error(apiError(e)); return null; } finally { setSaving(false); }
  }, [deck, id]);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); save(); } };
    const onLeave = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("keydown", onKey); window.addEventListener("beforeunload", onLeave);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("beforeunload", onLeave); };
  }, [save, dirty]);
  const model = useMemo(() => (deck ? computeModel(deck.assumptions, deck.exits) : null), [deck]);

  if (!deck) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const live = deck.access?.type !== "draft";
  const publishToggle = async () => {
    if (dirty && !(await save(true))) return;
    try { const { data } = await api.post(`/decks/${id}/${live ? "unpublish" : "publish"}`, {}); setDeck(data); toast.success(live ? "Deck unpublished" : `Published — arqitek.co/${data.slug}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (deck.embed_url) return (
    <div className="h-screen flex flex-col bg-background" data-testid="deck-builder">
      <AdminBar {...{ deck, dirty, saving, editing: false, setEditing: null, save, navigate, id, live, publishToggle, patch, setAccessOpen, setSettingsOpen }} />
      <iframe src={deck.embed_url} title={deck.name} className="flex-1 w-full border-0" data-testid="deck-builder-embed-frame" />
      {settingsOpen && <SettingsPanel deck={deck} patch={patch} onClose={() => setSettingsOpen(false)} />}
      {accessOpen && <AccessDialog deck={deck} open onOpenChange={setAccessOpen} onSaved={(d) => setDeck((x) => ({ ...x, access: d.access, updated_at: d.updated_at }))} />}
    </div>
  );
  return (
    <div className="h-screen flex flex-col bg-background" data-testid="deck-builder">
      <AdminBar {...{ deck, dirty, saving, editing, setEditing, save, navigate, id, live, publishToggle, patch, setAccessOpen, setSettingsOpen, onPresent: () => setPresenting(true) }} />
      <DeckShell deck={deck} admin editing={editing} onDeckChange={patch} library={library} heightClass="flex-1 min-h-0" />
      {settingsOpen && <SettingsPanel deck={deck} patch={patch} onClose={() => setSettingsOpen(false)} />}
      {presenting && <DeckPresenter deck={{ ...deck, sections: deck.sections.filter((s) => !s.hidden) }} model={model} onClose={() => setPresenting(false)} />}
      {accessOpen && <AccessDialog deck={deck} open onOpenChange={setAccessOpen} onSaved={(d) => setDeck((x) => ({ ...x, access: d.access, updated_at: d.updated_at }))} />}
    </div>
  );
}

function AdminBar({ deck, dirty, saving, editing, setEditing, save, navigate, id, live, publishToggle, patch, setAccessOpen, setSettingsOpen, onPresent }) {
  return (
    <div className="h-12 shrink-0 flex items-center gap-2 border-b border-border bg-card px-3 overflow-x-auto" data-testid="deck-admin-bar">
      <Button variant="ghost" size="sm" onClick={() => navigate("/decks")} data-testid="deck-builder-back"><ArrowLeft className="h-4 w-4 mr-1" />Decks</Button>
      <div className="min-w-0 hidden md:block">
        <div className="text-sm font-semibold truncate leading-tight" data-testid="deck-builder-name">{deck.name}</div>
        <div className="text-[10px] text-muted-foreground font-mono leading-tight">arqitek.co/{deck.slug} · rev {deck.revision}{dirty ? " · unsaved" : ""}</div>
      </div>
      {setEditing && (
        <button onClick={() => setEditing(!editing)} className={`ml-2 flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${editing ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`} data-testid="deck-edit-toggle">
          <span className={`h-3.5 w-6 rounded-full relative transition-colors ${editing ? "bg-primary" : "bg-muted"}`}><span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white transition-[left] ${editing ? "left-3" : "left-0.5"}`} /></span>
          {editing ? "Editing" : "Viewing"}
        </button>
      )}
      <select value={deck.status} onChange={(e) => patch({ status: e.target.value })} className="rounded-md border border-border bg-secondary/30 px-2 py-1 text-xs" data-testid="deck-builder-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      <div className="ml-auto flex gap-1.5">
        <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)} data-testid="deck-builder-settings">Settings</Button>
        <Button size="sm" variant={dirty ? "default" : "outline"} onClick={() => save()} disabled={saving || !dirty} data-testid="deck-builder-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save</Button>
        <Button size="sm" variant="outline" onClick={async () => { if (dirty) await save(true); window.open(`/decks/${id}/preview`, "_blank"); }} data-testid="deck-builder-preview"><Eye className="h-4 w-4 mr-1" />Preview</Button>
        {onPresent && <Button size="sm" variant="outline" onClick={onPresent} data-testid="deck-builder-present"><Play className="h-4 w-4 mr-1" />Present</Button>}
        <Button size="sm" variant="outline" onClick={() => setAccessOpen(true)} data-testid="deck-builder-access"><Shield className="h-4 w-4 mr-1" />Access</Button>
        <Button size="sm" onClick={publishToggle} data-testid="deck-builder-publish">{live ? <GlobeLock className="h-4 w-4 mr-1" /> : <Globe className="h-4 w-4 mr-1" />}{live ? "Unpublish" : "Publish"}</Button>
      </div>
    </div>
  );
}

function SettingsPanel({ deck, patch, onClose }) {
  const [tab, setTab] = useState("project");
  const model = computeModel(deck.assumptions, deck.exits);
  return (
    <div className="fixed inset-0 z-[110] flex justify-end" data-testid="deck-settings-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative h-full w-full sm:w-[460px] overflow-y-auto bg-background p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3"><div className="font-semibold">Deck settings</div><Button size="sm" variant="ghost" onClick={onClose} data-testid="deck-settings-close">Close</Button></div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full mb-4">
            <TabsTrigger value="project" data-testid="deck-tab-project">Project</TabsTrigger>
            <TabsTrigger value="exits" data-testid="deck-tab-exits">Exits</TabsTrigger>
            <TabsTrigger value="versions" data-testid="deck-tab-versions">History</TabsTrigger>
          </TabsList>
          <TabsContent value="project">
            <ProjectPanel project={deck.project} onChange={(project) => patch({ project, name: project.name || deck.name })} />
            <div className="mt-4 space-y-1">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Embedded deck URL (optional)</label>
              <input value={deck.embed_url || ""} onChange={(e) => patch({ embed_url: e.target.value })} placeholder="https://… existing presentation app" className="w-full rounded-md border border-border bg-secondary/30 px-2.5 py-1.5 text-sm font-mono" data-testid="deck-project-embed-url" />
            </div>
          </TabsContent>
          <TabsContent value="exits"><ExitsPanel exits={deck.exits} model={model} onChange={(exits) => patch({ exits })} /></TabsContent>
          <TabsContent value="versions"><VersionPanel deck={deck} /></TabsContent>
        </Tabs>
      </aside>
    </div>
  );
}
